import { Component, OnInit, ChangeDetectionStrategy, inject, input, effect } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { CreateCampaignLogicService } from '../../services/create-campaign-logic.service';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { filter, map, Observable, of, take } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';

// ponytail: TikTok-only, no group/page logic — add platform switch if FB follow needed later
const TIKTOK_USER_REGEX = /tiktok\.com\/@[^/?#]+|^@[a-zA-Z0-9_.]+$/;

@Component({
    selector: 'follow-user',
    templateUrl: './follow-user.component.html',
    styleUrls: ['./follow-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AsyncPipe, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatButtonModule],
    providers: [provideNativeDateAdapter()]
})
export class FollowUserComponent implements OnInit {

  applicationId = input<string | undefined>(undefined);
  ownerUnitId = input<string | undefined>(undefined);
  regionId = input<string | undefined>(undefined);
  gender = input<string | undefined>(undefined);
  ageFrom = input<number | undefined>(undefined);
  ageTo = input<number | undefined>(undefined);

  private appId$ = toObservable(this.applicationId);
  private ownerUnitId$ = toObservable(this.ownerUnitId);
  private regionId$ = toObservable(this.regionId);
  private gender$ = toObservable(this.gender);
  private ageFrom$ = toObservable(this.ageFrom);
  private ageTo$ = toObservable(this.ageTo);

  constructor() {
    effect(() => {
      this.applicationId();
      this.ownerUnitId();
      this.regionId();
      this.gender();
      this.ageFrom();
      this.ageTo();
      this.engagementFormGroup?.updateValueAndValidity();
    });
  }

  public engagementFormGroup: FormGroup = new FormGroup({});
  public countAvatarsInGroup$!: Observable<number | null>;

  private createCampaignService = inject(CreateCampaignLogicService);
  private fb = inject(FormBuilder);

  ngOnInit(): void {
    const now = new Date();
    const currentTime = now.getHours().toString().padStart(2, '0') + ':' +
                        now.getMinutes().toString().padStart(2, '0');

    this.engagementFormGroup = this.fb.group(
      {
        userUrl: [null, [Validators.required, Validators.pattern(TIKTOK_USER_REGEX)]],
        numberOfFollows: new FormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.pattern("^[0-9]*$")
        ]),
        secondsBetweenFollows: new FormControl(30, [
          Validators.required,
          Validators.min(0),
          Validators.pattern("^[0-9]*$")
        ]),
        startDate: [now, Validators.required],
        startTime: [currentTime, Validators.required]
      },
      {
        asyncValidators: [this.avatarsLimitValidator()]
      }
    );

    this.countAvatarsInGroup$ = this.createCampaignService.getAvatarCount$(
      this.appId$,
      this.ownerUnitId$,
      of(undefined) as Observable<string | undefined>,
      of('page') as Observable<'page' | 'group'>,
      this.regionId$,
      this.gender$,
      this.ageFrom$,
      this.ageTo$
    );
  }

  onlyNumbers(event: KeyboardEvent) {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (!pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  avatarsLimitValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const numberOfFollows = control.get('numberOfFollows')?.value || 0;

      if (numberOfFollows === 0) return of(null);

      return this.countAvatarsInGroup$.pipe(
        filter(val => val !== null),
        take(1),
        map(availableCount => {
          if (availableCount === -1) {
            return { avatarCountError: true };
          }
          if (availableCount !== null && availableCount < numberOfFollows) {
            return { insufficientAvatars: { available: availableCount, required: numberOfFollows } };
          }
          return null;
        })
      );
    };
  }
}
